import { useMemo, useState } from 'react';
import { isNotNullOrUndefined, isNullOrUndefined } from '@vy/utils';
import { RenewContractOption } from '../renew-contract-dialog-types/renew-contract-option';
import { useContractRenewalFlow } from './use-contract-renewal-flow';
import { RenewContractStepOptionSelector } from '../renew-contract-dialog-components/RenewContractStepOptionSelector';

export const useRenewContractDialogController = (
  contractId: string,
  isAutoRenewal: boolean,
  onDialogClose: () => void,
) => {
  const [selectedOption, setSelectedOption] = useState<RenewContractOption | null>(null);
  const [pendingOption, setPendingOption] = useState<RenewContractOption | null>(null);
  const [isAutoRenewalChecked, setIsAutoRenewalChecked] = useState<boolean>(isAutoRenewal);

  const resetDialog = () => {
    setSelectedOption(null);
    setPendingOption(null);
    onDialogClose();
  };

  const flowHandler = useContractRenewalFlow(selectedOption, contractId, isAutoRenewalChecked, resetDialog);

  const currentStepIndex = flowHandler?.currentStep ?? 0;
  const currentStepView = useMemo(
    () => flowHandler?.getStepView(currentStepIndex) ?? null,
    [flowHandler, currentStepIndex],
  );

  const isInStepFlow = isNotNullOrUndefined(selectedOption);
  const isOptionSelected = isNotNullOrUndefined(pendingOption);

  const handleNext = () => {
    if (!isInStepFlow && isOptionSelected) {
      return setSelectedOption(pendingOption);
    }

    if (isNotNullOrUndefined(currentStepView?.onNext)) {
      return currentStepView.onNext();
    }
  };

  const handleBack = () => {
    if (isNullOrUndefined(flowHandler)) {
      return;
    }

    if (flowHandler.isFirstStep) {
      setSelectedOption(null);
      setPendingOption(null);
      return;
    }

    flowHandler.goToPreviousStep();
  };

  const getDialogLabelDescription = () => {
    if (isNullOrUndefined(currentStepView)) {
      return 'Select one of the options to renew your contract';
    }

    return currentStepView.labelDescription;
  };

  const dialogProps = {
    label: currentStepView?.label ?? 'Renew contract',
    labelDescription: getDialogLabelDescription(),
    ctaText: currentStepView?.ctaText ?? 'Next',
    ctaDisabled: isInStepFlow ? false : !isOptionSelected,
    onSubmit: handleNext,
    onClose: resetDialog,
    navigateBack: isInStepFlow ? handleBack : undefined,
  };

  const content = isInStepFlow ? (
    currentStepView?.content
  ) : (
    <RenewContractStepOptionSelector
      onSelect={setPendingOption}
      selectedOption={pendingOption}
      setIsAutoRenewalChecked={setIsAutoRenewalChecked}
      isAutoRenewalChecked={isAutoRenewalChecked}
    />
  );

  return {
    dialogProps,
    content,
  };
};
